import { useRef, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/Atomic/Atoms/Icon'

import './DropZone.scss'

interface DropZoneProps {
  onOpen: () => void
  onFileDrop: (file: File) => void
  accept?: string
  chooseLabel?: string
  className?: string
}

export const DropZone: React.FC<DropZoneProps> = ({
  onOpen,
  onFileDrop,
  accept = '.csv',
  chooseLabel,
  className,
}) => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = (file: File | null | undefined) => {
    if (!file?.name.toLowerCase().endsWith(accept.toLowerCase())) return
    onFileDrop(file)
  }

  return (
    <div className={classNames(className)}>
      <div
        className={classNames('dropzone', { 'is-dragging': isDragging })}
        onClick={onOpen}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setIsDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          handleFile(e.dataTransfer.files[0])
        }}
        role="button"
        aria-label={t('organisms.dropZone.ariaLabel', 'Upload file')}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="dropzone-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
          onClick={(e) => e.stopPropagation()}
        />
        <Icon icon="upload" size="lg" className="dropzone-icon" />
        <p className="dropzone-label">
          {t('organisms.dropZone.label', 'Drag and drop or')}{' '}
          <span
            className="dropzone-link"
            onClick={(e) => {
              e.stopPropagation()
              inputRef.current?.click()
            }}
          >
            {chooseLabel ?? t('organisms.dropZone.chooseLabel', 'Choose file')}
          </span>{' '}
          {t('organisms.dropZone.suffix', 'to upload')}
        </p>
      </div>
    </div>
  )
}
